/**
 * Define elements to keep or remove based on various conditions.
 *
 * @module
 *
 * @author Jacob Viertel [jv@onscreen.net](mailto:jv@onscreen.net)
 */

// pure bounty
import {createComment} from '@acng/frontend-bounty';
import {add, createSet, remove, size, WeakStore} from '@acng/frontend-bounty/collection.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {replace} from '@acng/frontend-bounty/dom/move.js';

/**
 * @callback ConditionalFunction
 *
 * @param {boolean} condition
 *
 * @returns {void}
 */

/**
 * @callback ConditionalFactory
 *
 * @param {Element} element
 *
 * @returns {ConditionalFunction}
 */

/**
 * @type {(node: Node) => Comment}
 */
const getMark = WeakStore((node) => {
  const comment = createComment();

  DEBUG: {
    node = node.cloneNode();

    if (node instanceof Element) {
      node.textContent = '…';
      comment.data = node.outerHTML;
    } else {
      comment.data = node.nodeValue ?? '>- empty node -<';
    }
  }

  return comment;
});

/**
 * @type {(node: ChildNode) => Set<ConditionalFunction>}
 */
const getFalseConditions = WeakStore(() => createSet());

/**
 * Create a conditionally removing function for an element.
 *
 * @type {ConditionalFactory}
 */
const Conditional = (element) => {
  const falseConditions = getFalseConditions(element);
  const mark = getMark(element);

  /**
   * Conditionally remove the related element.
   *
   * @type {ConditionalFunction}
   */
  const toggle = (condition) => {
    if (condition) {
      DEBUG: if (debug(element)) console.debug('condition true', {element, condition});

      remove(falseConditions, toggle);
    } else {
      DEBUG: if (debug(element)) console.debug('condition false', {element, condition});

      add(falseConditions, toggle);
    }

    if (size(falseConditions)) {
      DEBUG: if (debug(element)) console.log('remove element', {element, falseConditions});

      replace(element, mark);
    } else {
      DEBUG: if (debug(element)) console.log('keep element', {element});

      replace(mark, element);
    }
  };

  return toggle;
};

export {Conditional};
